import type { CSSProperties, JSX } from 'react';

import { AppStyles } from '@/config/appStyles';
import { useL10n } from '@/l10n/useL10n';
import {
  SNACKBAR_TYPE_COLOR,
  snackbarTypeDefaultTitle,
  snackbarTypeDescriptionStyle,
  snackbarTypeTitleStyle,
  type SnackbarType,
} from './snackbarManager';
import styles from './CustomSnackbar.module.css';

/**
 * Перенос `lib/src/presentation/widgets/snackbar/custom_snackbar.dart`.
 *
 * Шрифтовые параметры берутся из `AppStyles` (единый источник, как
 * `TextStyle` в Dart), геометрия — из модуля CSS.
 */
export interface CustomSnackbarProps {
  readonly type: SnackbarType;
  readonly title?: string;
  readonly description?: string;

  /**
   * Код для справочника: посетителю ничего не говорит, но по фотографии
   * экрана инженер сразу видит, что чинить.
   */
  readonly code?: string;

  readonly onRetry?: () => void;
}

export function CustomSnackbar({
  type,
  title,
  description,
  code,
  onRetry,
}: CustomSnackbarProps): JSX.Element {
  const l10n = useL10n();
  const resolvedTitle = title ?? snackbarTypeDefaultTitle(type, l10n);

  // `IntrinsicHeight` не нужен: у flex-строки высота и так по содержимому,
  // а полоска тянется за счёт `align-items: stretch` (CrossAxisAlignment.stretch).
  return (
    <div className={styles.root}>
      <div
        className={styles.bar}
        style={{ '--bar-color': SNACKBAR_TYPE_COLOR[type] } as CSSProperties}
      />
      <div className={styles.content}>
        <span className={styles.title} style={snackbarTypeTitleStyle(type)}>
          {resolvedTitle}
        </span>
        {description !== undefined && (
          <span className={styles.description} style={snackbarTypeDescriptionStyle()}>
            {description}
          </span>
        )}
        {code !== undefined && (
          <span className={styles.code} style={AppStyles.s16w400}>
            {code}
          </span>
        )}
      </div>
      {onRetry !== undefined && (
        <div className={styles.retry}>
          <button
            type="button"
            className={styles.retryButton}
            style={AppStyles.s20w600}
            onClick={onRetry}
          >
            {l10n.retry}
          </button>
        </div>
      )}
    </div>
  );
}
